import { Drawer, Button, Empty, Descriptions, Badge, Tooltip, Modal, Form, Input, message, Grid } from 'antd';
import { FiBatteryCharging } from 'react-icons/fi';
import { BsInfoCircle } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useGetSingleTransaction, useSettleTransactionManually } from '../../Pages/charging-history/Hooks';
import { useOperatorStore } from '../../store/useOperatorStore';
import TransactionLogsAccordion from '../../Pages/charging-history/TransactionLogsAccordion';
import { formatCurrency, useIsEditAllowedWithOperator } from '../../utils/utils';
import { unixToLocalDate } from '../../utils/CommonFunctions';
import { useState } from 'react';
import SingleChargerDrawer from '../../Pages/locations/[id]/SingleChargerDrawer';
import CustomerDrawerView from '../CustomerDrawerView';
import LocationSingleDrawer from '../../Pages/locations/[id]/LocationSingleDrawer';
import { MdRefresh } from 'react-icons/md';
import { ExclamationCircleOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useDownloadPresignedUrl } from '../../Pages/report/Hook/Index';
import { createRoot } from 'react-dom/client';
import { getErrorMessage } from '../../utils/errorHandler';


const { useBreakpoint } = Grid;

const getBadgeStatus = (status) => {
  if (status === 'Completed') return 'success';
  if (status === 'Failed') return 'error';
  return 'processing';
};

const getSettlementStatus = (transaction, t) => {
  if (transaction?.isSettledManually) {
    return {
      status: 'success',
      text: t ? t('manuallySettled') : 'Manually Settled',
      icon: <CheckCircleOutlined />,
      color: 'green'
    };
  }
  return {
    status: 'default',
    text: t ? t('autoSettled') : 'Auto Settled',
    icon: <ClockCircleOutlined />,
    color: 'blue'
  };
};



const TransactionDetailsDrawer = ({ open, onClose, id }) => {
  const { t } = useTranslation(['transcationModal', 'common']);
  const { operatorId } = useOperatorStore();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { data: tempData, isLoading, refetch } = useGetSingleTransaction(
    { operatorId, id },
    { enabled: !!id }
  );
  const isEditAllowed = useIsEditAllowedWithOperator();
  // State for drawers (must be here to be in scope for buildSections)
  const [isLocationDrawerOpen, setLocationDrawerOpen] = useState(false);
  const [selectedLocationOwner, setSelectedLocationOwner] = useState(null);
  const [isChargerDrawerOpen, setChargerDrawerOpen] = useState(false);
  const [isCustomerDrawerOpen, setCustomerDrawerOpen] = useState(false);

  // State for manual settlement
  const [isSettleModalOpen, setIsSettleModalOpen] = useState(false);
  const [settleForm] = Form.useForm();
  const { mutate: settleTransaction, isPending: isSettling } = useSettleTransactionManually();
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);


  // Download hook for invoice files
  const { mutate: downloadPresignedUrl, isPending: isDownloadingInvoice } = useDownloadPresignedUrl({
    onSuccess: (data, variables) => {
      const downloadUrl = data.data;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', variables.fileName || 'invoice.pdf');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success(t('invoiceDownloadSuccess') || 'Invoice downloaded successfully');
    },
    onError: (error) => {
      message.error(getErrorMessage(error, t('invoiceDownloadError') || 'Failed to download invoice'));
    },
  });

  const handleDownloadFile = (fileUrl, fileName) => {
    downloadPresignedUrl({ fileUrl, fileName });
  };

  const mapTransactionToInvoiceData = () => null;

  const handleDownloadGeneratedInvoice = async () => {};

  const descriptionClassName = 'rounded-lg overflow-hidden bg-white';
  const descriptionLabelStyle = { fontWeight: 600, color: '#475569' };
  const descriptionContentStyle = { whiteSpace: 'nowrap' };
  const defaultSectionColumns = { xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 };
  const compactSectionColumns = { xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 };

  // Helper to build sections for Descriptions
  function buildSections(transaction, t, setChargerDrawerOpen, setCustomerDrawerOpen, formatCurrency) {
    const sections = [];

    if (transaction?.isRFID) {
      sections.push({
        title: t('rfidDetails'),
        items: [
          {
            label: t('batchId'),
            children: transaction?.idTag || 'N/A',
          },
        ],
      });
    }


    // General Information
    sections.push({
      title: t('generalInformation'),
      layout: 'vertical',
      column: { xs: 1, sm: 2, md: 3, lg: 6, xl: 6, xxl: 6 },
      items: [
        {
          label: t('transactionId'),
          children: transaction?.ocppTransactionId || 'N/A',
        },
        {
          label: t('status'),
          children: (
            <Badge
              status={getBadgeStatus(transaction?.status)}
              text={transaction?.status ? t(transaction.status) : 'N/A'}
            />
          ),
        },
        {
          label: t('settlementStatus'),
          children: (
            <Badge
              status={getSettlementStatus(transaction, t).status}
              text={getSettlementStatus(transaction, t).text}
              icon={getSettlementStatus(transaction, t).icon}
            />
          ),
        },
        {
          label: t('startedAt'),
          children: unixToLocalDate(transaction?.created_at) || 'N/A',
        },
        {
          label: t('endedAt'),
          children:
            transaction?.status !== 'inProgress' && transaction?.completed_at
              ? unixToLocalDate(transaction?.completed_at)
              : 'N/A',
        },
        {
          label: t('duration'),
          children:
            transaction?.status !== 'inProgress' &&
              transaction?.created_at &&
              transaction?.completed_at
              ? `${Math.round((transaction?.completed_at - transaction?.created_at) / 60)} ${t('common:minutesAbbrev')}`
              : 'N/A',
        },
      ],
    });
    

    // Charger Information
    sections.push({
      title: t('chargerInformation'),
      layout: 'vertical',
      column: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 },
      items: [
        {
          label: t('connectorId'),
          children: transaction?.chargerConnectorsData[0]?.connectorId || 'N/A',
        },
        {
          label: t('chargerName'),
          children: (
            <span
              className="text-[#067C60] hover:underline cursor-pointer"
              onClick={() => setChargerDrawerOpen(true)}
            >
              {transaction?.chargerData[0]?.name || 'N/A'} ({transaction?.serialNumber})
            </span>
          ),
        },

        {
          label: t('locationOwnerName',),
          children: transaction?.locationData?.[0]?.locationName ? (
            <span
              className="text-primary hover:underline cursor-pointer"
              onClick={() => {
                setSelectedLocationOwner(transaction.locationData[0]?._id);
                setLocationDrawerOpen(true);
              }}
            >
              {transaction.locationData[0].locationName}
            </span>
          ) : 'N/A',
        },
      ],
    });

    
    // Vehicle Information - Only show if there's actual data
    const hasCustomerData = transaction?.customerData?.[0]?.fname || transaction?.customerData?.[0]?.phoneNumber;
    const hasVehicleData = transaction?.vehicleData?.[0]?.name || transaction?.vehicleData?.[0]?.vehicleNumber;

    if (hasCustomerData || hasVehicleData) {
      sections.push({
        title: t('vehicleInformation'),
        column: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 },
        items: [
          ...(transaction?.customerData?.[0]?.fname ? [{
            label: t('customerName'),
            children: (
              <span
                className="text-[#067C60] font-semibold cursor-pointer"
                onClick={() => setCustomerDrawerOpen(true)}
              >
                {transaction?.customerData[0]?.fname}{' '}
                {transaction?.customerData[0]?.lname || ''}
              </span>
            ),
          }] : []),
          ...(transaction?.customerData?.[0]?.phoneNumber ? [{
            label: t('phoneNumber'),
            children: transaction?.customerData[0]?.phoneNumber,
          }] : []),
          ...(transaction?.vehicleData?.[0]?.name ? [{
            label: t('vehicleName'),
            children: transaction?.vehicleData[0]?.name,
          }] : []),
          ...(transaction?.vehicleData?.[0]?.vehicleNumber ? [{
            label: t('vehicleNumber'),
            children: transaction?.vehicleData[0]?.vehicleNumber,
          }] : []),
        ],
      });
    }

    // Transaction Metrics
    sections.push({
      title: t('transactionMetrics'),
      layout: 'vertical',
      column: defaultSectionColumns,
      items: [
        {
          label: t('consumption'),
          children: `${(transaction?.consumption / 1000).toFixed(4)} kWh`,
        },
        {
          label: t('startMeterReading'),
          children: transaction?.startMeterReading != null ? `${(Number(transaction.startMeterReading)).toFixed(4)} kWh` : 'N/A',
        },
        {
          label: t('endMeterReading'),
          children: transaction?.endMeterReading != null ? `${(Number(transaction.endMeterReading)).toFixed(4)} kWh` : 'N/A',
        },
        // Add current meter reading for interrupted transactions
        ...(transaction?.status === 'Interrupted' && transaction?.currentMeterReading ? [{
          label: t('currentMeterReading'),
          children: (
            <div>
              <div>{transaction.currentMeterReading != null && !isNaN(Number(transaction.currentMeterReading)) ? `${(Number(transaction.currentMeterReading)).toFixed(4)} kWh` : transaction.currentMeterReading}</div>
              {transaction.currentMeterReadingTime && (
                <div className="text-xs text-gray-500">
                  {unixToLocalDate(transaction.currentMeterReadingTime)}
                </div>
              )}
            </div>
          ),
        }] : []),
      ],
    });


    // Vehicle SOC Details - Only show if there's actual SOC data
    const hasStartSOC = transaction?.startVehiclePercentage !== undefined && transaction?.startVehiclePercentage !== null && transaction?.startVehiclePercentage !== 'N/A' && transaction?.startVehiclePercentage !== '';
    const hasCurrentSOC = transaction?.status === 'inProgress' && transaction?.vehiclePercentage !== undefined && transaction?.vehiclePercentage !== null && transaction?.vehiclePercentage !== 'N/A' && transaction?.vehiclePercentage !== '' && transaction?.vehiclePercentage !== 0;
    const hasEndSOC = transaction?.endVehiclePercentage !== undefined && transaction?.endVehiclePercentage !== null && transaction?.endVehiclePercentage !== 'N/A' && transaction?.endVehiclePercentage !== '';

    const hasSOCData = hasStartSOC || hasCurrentSOC || hasEndSOC;

    if (hasSOCData) {
      const socItems = [];

      if (hasStartSOC) {
        socItems.push({
          label: t('startSOCPercentage'),
          children: `${transaction?.startVehiclePercentage}%`,
        });
      }

      if (hasCurrentSOC) {
        socItems.push({
          label: t('currentSOCPercentage'),
          children: `${transaction?.vehiclePercentage}%`,
        });
      }

      if (hasEndSOC) {
        socItems.push({
          label: t('endSOCPercentage'),
          children: `${transaction?.endVehiclePercentage}%`,
        });
      }

      sections.push({
        title: t('vehicleSOCDetails'),
        column: { xs: 1, sm: 1, md: 1, lg: 3, xl: 3, xxl: 3 },
        layout: 'vertical',
        items: socItems,
      });
    }


    // Financial Details
    if (!transaction?.isRFID) {
      sections.push({
        title: t('financialDetails'),
        layout: 'vertical',
        column: defaultSectionColumns,
        items: [
          {
            label: (
              <span className="flex flex-row items-center gap-2">
                {t('netAmount')}
                <Tooltip title={t('netAmountTooltip')}>
                  <BsInfoCircle className="text-gray-500 cursor-pointer" />
                </Tooltip>
              </span>
            ),
            children: formatCurrency(transaction?.amount?.toFixed(2)) || 'N/A',
          },
          ...(transaction?.freeTierEnabled
            ? [
                {
                  label: t('freeTierAppliedKwh'),
                  children:
                    transaction?.freeTierAppliedWh != null
                      ? `${(Number(transaction.freeTierAppliedWh) / 1000).toFixed(3)} kWh`
                      : 'N/A',
                },
                {
                  label: t('freeTierAppliedAmount'),
                  children:
                    formatCurrency(
                      Number(transaction?.freeTierAppliedAmount || 0).toFixed(2),
                    ) || 'N/A',
                },
                {
                  label: t('freeTierSwitchedToPaidAt'),
                  children: transaction?.freeTierSwitchedToPaidAt
                    ? unixToLocalDate(transaction.freeTierSwitchedToPaidAt)
                    : t('stillOnFreeTier'),
                },
              ]
            : []),
          ...(transaction?.idleFeeEnabled || Number(transaction?.idleTotalCost) > 0
            ? [
                {
                  label: t('idleBillableMinutes'),
                  children:
                    transaction?.idleBillableMinutes != null
                      ? String(transaction.idleBillableMinutes)
                      : 'N/A',
                },
                {
                  label: t('idleAmount'),
                  children:
                    formatCurrency(
                      Number(transaction?.idleAmount || 0).toFixed(2),
                    ) || 'N/A',
                },
                {
                  label: t('idleTaxAmount'),
                  children:
                    formatCurrency(
                      Number(transaction?.idleTaxAmount || 0).toFixed(2),
                    ) || 'N/A',
                },
                {
                  label: t('idleTotalCost'),
                  children:
                    formatCurrency(
                      Number(transaction?.idleTotalCost || 0).toFixed(2),
                    ) || 'N/A',
                },
              ]
            : []),
          {
            label: (
              <span className="flex flex-row items-center gap-2">
                {t('vatRate')}
                <Tooltip title={t('vatRateTooltip')}>
                  <BsInfoCircle className="text-gray-500 cursor-pointer" />
                </Tooltip>
              </span>
            ),
            children:
              transaction?.vatRate != null
                ? `${Number(transaction.vatRate).toFixed(2)}%`
                : 'N/A',
          },
          {
            label: (
              <span className="flex flex-row items-center gap-2">
                {t('taxAmount')}
                <Tooltip title={t('taxAmountTooltip')}>
                  <BsInfoCircle className="text-gray-500 cursor-pointer" />
                </Tooltip>
              </span>
            ),
            children: formatCurrency(transaction?.taxAmount?.toFixed(2)) || 'N/A',
          },
          {
            label: (
              <span className="flex flex-row items-center gap-2">
                {t('grossAmount')}
                <Tooltip title={t('grossAmountTooltip')}>
                  <BsInfoCircle className="text-gray-500 cursor-pointer" />
                </Tooltip>
              </span>
            ),
            children: formatCurrency(transaction?.totalCost?.toFixed(2)) || 'N/A',
          },
          {
            label: (
              <span className="flex flex-row items-center gap-2">
                {t('refundAmount')}
                <Tooltip title={t('refundAmountTooltip')}>
                  <BsInfoCircle className="text-gray-500 cursor-pointer" />
                </Tooltip>
              </span>
            ),
            children: formatCurrency(transaction?.refundAmount?.toFixed(2)) || 'N/A',
          },
          {
            label: (
              <span className="flex flex-row items-center gap-2">
                {t('commission')}
                <Tooltip
                  title={`${t('commissionTooltip')} ${t('operatorCommissionDetails')}`}
                >
                  <BsInfoCircle className="text-gray-500 cursor-pointer" />
                </Tooltip>
              </span>
            ),
            children: formatCurrency(transaction?.commission),
          },
          {
            label: (
              <span className="flex flex-row items-center gap-2">
                {t('locationOwnerEarning')}
                <Tooltip title={t('locationOwnerEarningTooltip')}>
                  <BsInfoCircle className="text-gray-500 cursor-pointer" />
                </Tooltip>
              </span>
            ),
            children:
              formatCurrency(transaction?.locationOwnerEaring?.toFixed(2)) || 'N/A',
          },
        ],
      });
    }

    // Configured Values - Title removed per user request (kept inside same conditional)
    if (!transaction?.isRFID) {
      sections.push({
        title: null, // Title removed as requested
        layout: 'vertical',
        column: compactSectionColumns,
        items: [
          {
            label: t('perUnitCharges'),
            children: transaction?.touSlotBreakdown?.length ? (
              <div className="text-sm">
                {transaction.touSlotBreakdown.map((s, i) => (
                  <div key={i}>
                    {s.startTime}-{s.endTime}: {formatCurrency(s.rate)}/kWh ×{' '}
                    {Number(s.consumptionKwh || 0).toFixed(3)} kWh ={' '}
                    {formatCurrency(s.amount)}
                  </div>
                ))}
              </div>
            ) : (
              formatCurrency(transaction?.perUnitCharges?.toFixed(2)) || 'N/A'
            ),
          },
          // Company Commission Percentage hidden per user request
        ],
      });
    }

    // Additional Details
    const additionalItems = [
      {
        label: t('isRFID'),
        children: transaction?.isRFID ? t('yes') : t('no'),
      },
      ...(transaction?.customerReason && transaction?.customerReason !== '0' ? [
        {
          label: t('customerReason'),
          children: transaction?.customerReason,
        },
      ] : []),
      ...(transaction?.status === 'Interrupted' || transaction?.status === 'Failed' || (transaction?.failedReason && transaction?.failedReason !== '0')
        ? [
          {
            label: t('failedReason'),
            children: (transaction?.failedReason && transaction?.failedReason !== '0') ? transaction?.failedReason : t('notProvided'),
          },
        ]
        : []),
    ];

    // Add manual settlement details if transaction was manually settled
    if (transaction?.isSettledManually) {
      additionalItems.push(
        {
          label: t('settledManuallyAt'),
          children: unixToLocalDate(transaction?.settledManuallyAt) || 'N/A',
        },
        {
          label: t('settledManuallyBy'),
          children: transaction?.settledManuallyBy || 'N/A',
        },
        {
          label: t('settledManuallyReason'),
          children: transaction?.settledManuallyReason || 'N/A',
        }
      );
    }

    sections.push({
      title: t('additionalDetails'),
      layout: 'vertical',
      column: defaultSectionColumns,
      items: additionalItems,
    });

    return sections;
  }

  // Function to build pre-settled data section
  function buildPreSettledSection(transaction, t, formatCurrency) {
    if (!transaction?.isSettledManually || !transaction?.preSettledData) {
      return null;
    }

    const preSettledData = transaction.preSettledData;

    return {
      title: t('preSettledData'),
      layout: 'vertical',
      column: defaultSectionColumns,
      items: [
        {
          label: t('preSettledStatus'),
          children: (
            <Badge
              status={getBadgeStatus(preSettledData?.status)}
              text={preSettledData?.status || 'N/A'}
            />
          ),
        },
        {
          label: t('preSettledConsumption'),
          children: `${(preSettledData?.consumption / 1000).toFixed(4)} kWh`,
        },
        {
          label: t('preSettledStartMeterReading'),
          children: preSettledData?.startMeterReading != null ? `${(Number(preSettledData.startMeterReading)).toFixed(4)} kWh` : 'N/A',
        },
        {
          label: t('preSettledEndMeterReading'),
          children: preSettledData?.endMeterReading != null ? `${(Number(preSettledData.endMeterReading)).toFixed(4)} kWh` : 'N/A',
        },
        {
          label: t('preSettledAmount'),
          children: formatCurrency(preSettledData?.amount?.toFixed(2)) || 'N/A',
        },
        {
          label: t('preSettledTotalCost'),
          children: formatCurrency(preSettledData?.totalCost?.toFixed(2)) || 'N/A',
        },
        {
          label: t('preSettledTaxAmount'),
          children: formatCurrency(preSettledData?.taxAmount?.toFixed(2)) || 'N/A',
        },
        {
          label: t('preSettledFailedReason'),
          children: (preSettledData?.failedReason && preSettledData?.failedReason !== '0') ? preSettledData?.failedReason : 'N/A',
        },
        {
          label: t('preSettledRequiredTime'),
          children: preSettledData?.requiredTime ? `${preSettledData.requiredTime.toFixed(2)} ${t('common:minutesAbbrev')}` : 'N/A',
        },
      ],
    };
  }

  const transaction = tempData;

  // Fix: Extract nested ternary operation into an independent statement
  let sectionElements;
  if (transaction) {
    const sections = buildSections(
      transaction,
      t,
      setChargerDrawerOpen,
      setCustomerDrawerOpen,
      formatCurrency,
      handleDownloadFile,
      isDownloadingInvoice,
      handleDownloadGeneratedInvoice,
      isGeneratingInvoice
    );

    // Separate General Information and Charger Information for left column
    const generalInfoSection = sections.find(s => s.title === t('generalInformation'));
    const chargerInfoSection = sections.find(s => s.title === t('chargerInformation'));

    // Get all other sections (excluding general and charger info)
    const otherSections = sections.filter(
      s => s.title !== t('generalInformation') && s.title !== t('chargerInformation')
    );

    // Add pre-settled data section if transaction was manually settled
    const preSettledSection = buildPreSettledSection(transaction, t, formatCurrency);
    if (preSettledSection) {
      otherSections.push(preSettledSection);
    }

    // Render left column sections (General Info and Charger Info)
    const leftColumnSections = [generalInfoSection, chargerInfoSection].filter(Boolean);
    const leftColumnElements = leftColumnSections.map((section) => (
      <div key={section.title} className="mb-6">
        <Descriptions
          title={section.title}
          bordered
          size="small"
          layout={section.layout || 'horizontal'}
          column={section.column}
          items={section.items}
          className={descriptionClassName}
          labelStyle={descriptionLabelStyle}
          contentStyle={descriptionContentStyle}
        />
      </div>
    ));

    const otherSectionElements = [];

    otherSections.forEach((section) => {
      // Skip rendering if title is null (removed section)
      if (section.title === null && section.items.length === 0) {
        return;
      }
      otherSectionElements.push(
        <div key={section.title || 'no-title'} className="mb-6">
          <Descriptions
            title={section.title || undefined}
            bordered
            size="small"
            layout={section.layout || 'horizontal'}
            column={section.column}
            items={section.items}
            className={descriptionClassName}
            labelStyle={descriptionLabelStyle}
            contentStyle={descriptionContentStyle}
          />
        </div>
      );
    });

    // Combine everything with single column layout for General Info and Charger Info
    sectionElements = (
      <>
        {/* Single column layout for General Info and Charger Info */}
        {leftColumnElements.length > 0 && (
          <div className="mb-6">
            {leftColumnElements}
          </div>
        )}
        {/* Other sections below */}
        {otherSectionElements}
      </>
    );
  } else {
    sectionElements = <Empty description={t('noTransactionDetailsAvailable')} />;
  }

  return (
    <Drawer
      maskClosable={false}
      title={
        <div className="flex justify-between text-lg font-semibold items-center">
          <div className="flex flex-row gap-4 items-center">
            <FiBatteryCharging className="text-green-500" />
            <span>{t('transactionDetails')} </span>
          </div>
          <div className="flex flex-row gap-2">
            {tempData?.status !== 'InProgress' && tempData?.status !== 'Failed' && !tempData?.isSettledManually && isEditAllowed && (
              <Tooltip title={t('settleManually')}>
                <Button
                  type="primary"
                  danger
                  icon={<ExclamationCircleOutlined />}
                  onClick={() => setIsSettleModalOpen(true)}
                >
                  {t('settleManually')}
                </Button>
              </Tooltip>
            )}
            <Tooltip title={t('refresh')}>
              <Button icon={<MdRefresh />} onClick={() => refetch()} />
            </Tooltip>
          </div>
        </div>
      }
      placement="right"
      width={isMobile ? '100%' : "80%"}
      loading={isLoading}
      onClose={onClose}
      open={open}
    >
      {sectionElements}

      {/* Drawers */}
      {isChargerDrawerOpen && (
        <SingleChargerDrawer
          isOpen={isChargerDrawerOpen}
          onClose={() => setChargerDrawerOpen(false)}
          selectedCharger={transaction?.chargerId}
          passoperatorId={transaction?.operatorId}
        />
      )}
      {isCustomerDrawerOpen && (
        <CustomerDrawerView
          open={isCustomerDrawerOpen}
          onClose={() => setCustomerDrawerOpen(false)}
          id={
            transaction?.customerData?.[0]?._id ??
            transaction?.customerData?.[0]?.id ??
            transaction?.customerId
          }
          operatorId={transaction?.operatorId}
        />
      )}

      {isLocationDrawerOpen && (
        <LocationSingleDrawer
          isOpen={isLocationDrawerOpen}
          selectedLocation={selectedLocationOwner}
          OPId={transaction?.operatorId}
          onClose={() => { setSelectedLocationOwner(null); setLocationDrawerOpen(false) }}
        />
      )}

      <TransactionLogsAccordion operatorId={operatorId} transactionId={id} />

      {/* Manual Settlement Modal */}
      <Modal
        title={t('settleManuallyTitle')}
        open={isSettleModalOpen}
        onCancel={() => {
          setIsSettleModalOpen(false);
          settleForm.resetFields();
        }}
        centered
        footer={[
          <Button key="cancel" onClick={() => {
            setIsSettleModalOpen(false);
            settleForm.resetFields();
          }}>
            {t('settleManuallyCancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={isSettling}
            onClick={() => settleForm.submit()}
          >
            {t('settleManuallyConfirm')}
          </Button>,
        ]}
        width={isMobile ? '90%' : 600}
      >
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-start gap-2">
            <ExclamationCircleOutlined className="text-yellow-600 mt-1" />
            <div>
              <p className="text-sm text-yellow-800 font-medium">{t('settleManuallyWarning')}</p>
              <p className="text-xs text-yellow-700 mt-1">{t('settleManuallyDescription')}</p>
            </div>
          </div>
        </div>

        <Form
          form={settleForm}
          layout="vertical"
          onFinish={(values) => {
            settleTransaction(
              {
                transactionId: id,
                data: {
                  settledManuallyReason: values.reason,
                  startMeterReading: values.startMeterReading,
                  endMeterReading: values.endMeterReading,
                },
              },
              {
                onSuccess: () => {
                  message.success(t('settleManuallySuccess'));
                  setIsSettleModalOpen(false);
                  settleForm.resetFields();
                  refetch();
                },
                onError: (error) => {
                  message.error(error?.response?.data?.message || t('settleManuallyError'));
                },
              }
            );
          }}
          initialValues={{
            startMeterReading: tempData?.startMeterReading || '',
            endMeterReading: tempData?.currentMeterReading || tempData?.endMeterReading || '',
          }}
        >
          {/* Current Meter Reading Information */}
          {tempData?.currentMeterReading && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start gap-2">
                <div className="text-blue-600 mt-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-blue-800 font-medium">Current Meter Reading Information</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Current Reading: <strong>{tempData.currentMeterReading != null && !isNaN(Number(tempData.currentMeterReading)) ? `${(Number(tempData.currentMeterReading)).toFixed(4)} kWh` : tempData.currentMeterReading}</strong>
                    {tempData.currentMeterReadingTime && (
                      <span> • Time: {unixToLocalDate(tempData.currentMeterReadingTime)}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Form.Item
            name="reason"
            label={t('settleManuallyReason')}
            rules={[
              {
                required: true,
                message: t('settleManuallyReasonPlaceholder'),
              },
            ]}
          >
            <Input.TextArea
              placeholder={t('settleManuallyReasonPlaceholder')}
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="startMeterReading"
            label={t('settleManuallyStartMeter')}
            rules={[
              {
                required: true,
                message: t('settleManuallyStartMeterPlaceholder'),
              },
              {
                pattern: /^\d+(\.\d+)?$/,
                message: t('invalidNumberError'),
              },
            ]}
          >
            <Input
              placeholder={t('settleManuallyStartMeterPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="endMeterReading"
            label={t('settleManuallyEndMeter')}
            dependencies={['startMeterReading']}
            rules={[
              {
                required: true,
                message: t('settleManuallyEndMeterPlaceholder'),
              },
              {
                pattern: /^\d+(\.\d+)?$/,
                message: t('invalidNumberError'),
              },
              {
                validator: (_, value) => {
                  const startReading = settleForm.getFieldValue('startMeterReading');
                  if (startReading && value && parseFloat(value) < parseFloat(startReading)) {
                    return Promise.reject(new Error(t('meterReadingValidationError')));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={t('settleManuallyEndMeterPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
};

TransactionDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default TransactionDetailsDrawer;
